import type { VoiceCredentials, VoiceClient } from './VoiceClient';

/** VoiceControls 依赖的最小结构：真实 VoiceClient 与下面的懒加载壳都满足。 */
export interface VoiceController {
    micEnabled: boolean;
    /** 房间级禁言（主持人）：true 时麦克风按钮锁定为闭麦态。 */
    adminMuted: boolean;
    onHint: (message: string) => void;
    onMicState: (enabled: boolean) => void;
    join(creds: VoiceCredentials): Promise<void>;
    toggleMic(): Promise<boolean>;
    /** 打开麦克风（主持人远程开麦 player:mic-open 复用；不影响已开麦状态）。 */
    openMic(): Promise<boolean>;
    /** 关闭麦克风（主持人远程关麦 player:mic-close 复用；已关麦时幂等）。 */
    closeMic(): Promise<boolean>;
    setAdminMuted(muted: boolean): Promise<void>;
    leave(): Promise<void>;
    dispose(): void;
}

/**
 * VoiceClient 懒加载壳：agora SDK（约 1.6MB / gzip 440KB）不打进主包，
 * 首次真正要进语音频道时才用 import() 下载（时机由 main.ts 控制：首个
 * SCENE_READY 之后，避开进场阻塞 LOD 块的带宽窗口）。浏览器缓存后再次
 * 进房瞬时可用；solo 世界场景永远不会下载。下载失败静默降级（语音按钮
 * 不出现），并允许下次重试。
 */
export class LazyVoice implements VoiceController {
    micEnabled = false;
    /** 禁言标志在 SDK 未加载时也要先记住：ready() 后同步给真实客户端。 */
    adminMuted = false;
    onHint: (message: string) => void = () => {};
    onMicState: (enabled: boolean) => void = () => {};
    private impl?: VoiceClient;
    private loading?: Promise<VoiceClient | undefined>;

    private ready(): Promise<VoiceClient | undefined> {
        if (this.impl) return Promise.resolve(this.impl);
        return this.loading ??= import('./VoiceClient').then(
            ({ VoiceClient }) => {
                const client = new VoiceClient();
                client.onHint = (msg) => this.onHint(msg);
                client.onMicState = (enabled) => { this.micEnabled = enabled; this.onMicState(enabled); };
                // SDK 此刻才加载（用户此前从未开麦）：把壳上缓存的禁言状态带进
                // 新客户端；复用已有客户端时不在此同步——换房 leave 已按房间重置。
                if (this.adminMuted) void client.setAdminMuted(true);
                this.impl = client;
                return client;
            },
            (err) => {
                console.warn('[voice] SDK 加载失败，本次会话语音停用', err);
                this.loading = undefined; // 瞬时网络问题不至于永久关死
                return undefined;
            },
        );
    }

    async join(creds: VoiceCredentials): Promise<void> {
        const client = await this.ready();
        if (client) await client.join(creds);
    }

    async toggleMic(): Promise<boolean> {
        const client = await this.ready();
        return client ? client.toggleMic() : false;
    }

    async openMic(): Promise<boolean> {
        const client = await this.ready();
        return client ? client.openMic() : false;
    }

    async closeMic(): Promise<boolean> {
        const client = await this.ready();
        return client ? client.closeMic() : false;
    }

    setAdminMuted(muted: boolean): Promise<void> {
        this.adminMuted = muted;
        return this.impl ? this.impl.setAdminMuted(muted) : Promise.resolve();
    }

    async leave(): Promise<void> {
        // 禁言随房间：与实现侧 leave() 同时重置壳标志，换房后以新房间快照为准。
        this.adminMuted = false;
        await this.impl?.leave();
    }

    dispose(): void {
        this.impl?.dispose();
    }
}
